import React, {lazy, Suspense, useEffect} from 'react';
import {Navigate, Outlet, Route, Routes, useLocation, useNavigate} from 'react-router-dom';
import {alertTypes} from '@store/types';
import {useExpiresToken} from '@hooks/useExpiresToken';
import {ModalProvider, useModal} from '@ui/Modal';
import {selectBackendAvailable, selectToken, setToken} from '@store/app.slice';
import {useAppAlert, useAppDispatch, useAppSelector} from './hooks';
import {ErrorPage} from './pages/ErrorPage';
import {Redirect} from './pages/Redirect';
import {MainLayout} from './layouts';
import CompanyLayout from './pages/Companies/CompanyLayout';
import {CheckAuthDialog, LogoutNotificationDialog} from './components/CheckAuthDialog';
import {SiteVisitTracker} from './components/SiteVisitTracker';
import {OnlinePingTracker} from './components/OnlinePingTracker';
import {MaintenanceScreen} from './components/MaintenanceScreen';
import {useGetUserProfileQuery} from './pages/Profile/Profile.api';

const TestPage = lazy(() => import('./pages/TestPage'));
const HomePage = lazy(() => import('./pages/Home/Home'));
const CatalogPage = lazy(() => import('./pages/Catalog/CatalogPage'));
const LoginPage = lazy(() => import('./pages/Auth/LoginPage'));
const RegistrationPage = lazy(() => import('./pages/Auth/RegistrationPage'));
const RecoveryPasswordPage = lazy(() => import('./pages/Auth/RecoveryPasswordPage'));
const NewPasswordPage = lazy(() => import('./pages/Auth/NewPasswordPage'));

const TiresPage = lazy(() => import('./pages/Search/Tires/TyresSearchPage'));
const DisksPage = lazy(() => import('./pages/Search/Disks/DisksSearchPage'));
const SpecialTyresPage = lazy(() => import('./pages/Search/SpecialTyres/SpecialTyresSearchPage'));
const TruckTiresPage = lazy(() => import('./pages/Search/TruckTyres/TruckTyresSearchPage'));
const TruckDisksPage = lazy(() => import('./pages/Search/TruckDisks/TruckDisksSearchPage'));
const MotoTyresPage = lazy(() => import('./pages/Search/MotoTyres/MotoTyresSearchPage'));
const QuadroTyresPage = lazy(() => import('./pages/Search/QuadroTyres/QuadroTyresSearchPage'));

const ProfilePage = lazy(() => import('./pages/Profile/ProfilePage'));
const SuppliersPage = lazy(() => import('./pages/Search/Suppliers/SuppliersSearchPage'));
const SupplierPage = lazy(() => import('./pages/Supplier/SupplierPage'));
const FavoriteSuppliersPage = lazy(() => import('./pages/FavoriteSuppliers/FavoriteSuppliersPage'));
const CartPage = lazy(() => import('./pages/Cart/CartPage'));
const SharedRestsPage = lazy(() => import('./pages/SharedRests/SharedRestsPage'));

const App = () => {
    const {initAlert} = useAppAlert();
    const {errorReq, successReq} = useAppSelector(state => state.app);
    const backendAvailable = useAppSelector(selectBackendAvailable);

    useEffect(() => {
        errorReq && initAlert({
            type: alertTypes.ERROR,
            text: errorReq?.message ?? '',
        });
        successReq && initAlert({
            type: alertTypes.SUCCESS,
            text: successReq?.message ?? '',
        });
    }, [errorReq, successReq]);

    if (!backendAvailable) {
        return <MaintenanceScreen/>;
    }

    return (
        <Routes>
            <Route path='/' element={<Suspense children={<ModalProvider><CheckAuthDialog/><SiteVisitTracker/><OnlinePingTracker/><Outlet/></ModalProvider>}/>}>
                {/*<Route path='/test' element={<TestPage/>}/>*/}

                <Route path='/login' element={<LoginPage/>}/>
                <Route path='/registration' element={<RegistrationPage/>}/>
                <Route path='/recovery-password' element={<RecoveryPasswordPage/>}/>
                <Route path='/reset-password' element={<NewPasswordPage/>}/>

                <Route path='/' element={<HomePage/>}/>
                <Route path='/catalog' element={<CatalogPage/>}/>
                <Route path='/tires' element={<TiresPage/>}/>
                <Route path='/disks' element={<DisksPage/>}/>
                <Route path='/special_tyres' element={<SpecialTyresPage/>}/>
                <Route path='/truck_tires' element={<TruckTiresPage/>}/>
                <Route path='/truck_disks' element={<TruckDisksPage/>}/>
                <Route path='/moto_tires' element={<MotoTyresPage/>}/>
                <Route path='/quadro_tires' element={<QuadroTyresPage/>}/>
                <Route path='/suppliers' element={<SuppliersPage/>}/>
                <Route path='/shared/product/:productId/rests' element={<SharedRestsPage/>}/>

                <Route element={<Suspense children={<PrivateRoute/>}/>}>
                    <Route path='/profile' element={<ProfilePage/>}/>
                    {/*<Route path='/companies' element={<MainLayout children={<Outlet/>}/>}>
                        <Route path='' element={<CompaniesPage/>}/>
                        <Route path=':company' element={<CompanyLayout children={<Outlet/>}/>}>
                            <Route path='edit' element={<CompaniesEditPage/>}/>
                            <Route path='employees' element={<EmployeesPage/>}/>
                            <Route path='rests' element={<UploadRestsPage/>}/>
                        </Route>
                        <Route path='create' element={<CompanyLayout children={<CompaniesEditPage/>}/>}/>
                    </Route>*/}
                    <Route path='*' element={<ErrorPage/>}/>
                    <Route path='/redirect' element={<Redirect/>}/>
                    <Route path='/suppliers/:id' element={<SupplierPage/>}/>
                    <Route path='/favorite-suppliers' element={<FavoriteSuppliersPage/>}/>
                    <Route path='/cart' element={<CartPage/>}/>
                </Route>
            </Route>
        </Routes>
    );
};

function PrivateRoute() {
    const location = useLocation();
    const navigation = useNavigate();
    const {not_expires} = useExpiresToken();
    const {token} = useAppSelector(state => state.app);

    useEffect(() => {
        const isOuterTransition: string = location.search;
        if (isOuterTransition !== '' && token) {
            navigation(isOuterTransition);
        }
    }, [token]);

    if (token && not_expires) {
        return <Outlet/>;
    } else {
        return <Navigate to={'/login'}/>;
    }
}

export default App;
